import { useState } from 'react';

import type {
  Candidate,
  DraftFormState,
  Spotlight,
  SpotlightFeaturedReview,
} from './types';
import {
  candidateToEntry,
  defaultDraftForm,
  packageKey,
  toDateTimeLocal,
} from './utils';

export const MAX_SPOTLIGHT_APPS = 8;

export function spotlightToForm(spotlight: Spotlight): DraftFormState {
  return {
    id: spotlight.id,
    weekId: spotlight.weekId,
    themeTitle: spotlight.themeTitle,
    headerBlurb: spotlight.headerBlurb,
    publishStartAt: toDateTimeLocal(spotlight.publishStartAt),
    publishEndAt: toDateTimeLocal(spotlight.publishEndAt),
    appEntries: spotlight.appEntries.map((entry) => ({
      androidPackage: entry.androidPackage,
      curationNote: entry.curationNote ?? '',
      featuredReview: entry.featuredReview ?? null,
    })),
    sourceCandidateRunId: spotlight.sourceCandidateRunId,
  };
}

export function useDraftForm(initial: Spotlight | null) {
  const [seed, setSeed] = useState<DraftFormState>(() =>
    initial ? spotlightToForm(initial) : defaultDraftForm(),
  );
  const [form, setForm] = useState<DraftFormState>(seed);

  const isDirty = JSON.stringify(form) !== JSON.stringify(seed);
  const canAddApp = form.appEntries.length < MAX_SPOTLIGHT_APPS;

  const setField = <K extends keyof DraftFormState>(
    key: K,
    value: DraftFormState[K],
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const addApp = (androidPackage: string) => {
    setForm((current) => {
      if (current.appEntries.length >= MAX_SPOTLIGHT_APPS) {
        return current;
      }
      const exists = current.appEntries.some(
        (entry) =>
          packageKey(entry.androidPackage) === packageKey(androidPackage),
      );
      if (exists) {
        return current;
      }
      return {
        ...current,
        appEntries: [
          ...current.appEntries,
          { androidPackage, curationNote: '', featuredReview: null },
        ],
      };
    });
  };

  const addCandidate = (candidate: Candidate) => {
    setForm((current) => {
      if (current.appEntries.length >= MAX_SPOTLIGHT_APPS) {
        return current;
      }
      const exists = current.appEntries.some(
        (entry) =>
          packageKey(entry.androidPackage) ===
          packageKey(candidate.androidPackage),
      );
      if (exists) {
        return current;
      }
      return {
        ...current,
        appEntries: [...current.appEntries, candidateToEntry(candidate)],
      };
    });
  };

  const removeApp = (index: number) => {
    setForm((current) => {
      if (index < 0 || index >= current.appEntries.length) {
        return current;
      }
      const next = current.appEntries.slice();
      next.splice(index, 1);
      return { ...current, appEntries: next };
    });
  };

  const moveApp = (from: number, to: number) => {
    if (from === to) {
      return;
    }
    setForm((current) => {
      if (
        from < 0 ||
        from >= current.appEntries.length ||
        to < 0 ||
        to >= current.appEntries.length
      ) {
        return current;
      }
      const next = current.appEntries.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return { ...current, appEntries: next };
    });
  };

  const setCurationNote = (index: number, value: string) => {
    setForm((current) => {
      if (index < 0 || index >= current.appEntries.length) {
        return current;
      }
      const next = current.appEntries.slice();
      next[index] = { ...next[index], curationNote: value };
      return { ...current, appEntries: next };
    });
  };

  const setFeaturedReview = (
    index: number,
    review: SpotlightFeaturedReview | null,
  ) => {
    setForm((current) => {
      if (index < 0 || index >= current.appEntries.length) {
        return current;
      }
      const next = current.appEntries.slice();
      next[index] = { ...next[index], featuredReview: review };
      return { ...current, appEntries: next };
    });
  };

  const reseed = (spotlight: Spotlight) => {
    const next = spotlightToForm(spotlight);
    setSeed(next);
    setForm(next);
  };

  return {
    form,
    isDirty,
    canAddApp,
    setField,
    addApp,
    addCandidate,
    removeApp,
    moveApp,
    setCurationNote,
    setFeaturedReview,
    reseed,
  };
}

export type DraftFormController = ReturnType<typeof useDraftForm>;
